/**
 * ValidationUtils - Comprehensive validation utilities
 * 
 * Features:
 * - Email validation with proper regex
 * - Password strength validation
 * - Phone number validation for Bulgarian numbers
 * - Betting-specific validations (stakes, odds)
 * - Form field validation helpers
 * - Validation error message generation
 */

/**
 * Validation result structure
 */
export class ValidationResult {
  constructor(isValid = false, errors = []) {
    this.isValid = isValid;
    this.errors = Array.isArray(errors) ? errors : [errors];
  }

  addError(error) {
    this.errors.push(error);
    this.isValid = false;
    return this;
  }

  addErrors(errors) {
    if (Array.isArray(errors)) {
      this.errors.push(...errors);
    } else {
      this.errors.push(errors);
    }
    this.isValid = false;
    return this;
  }

  static success() {
    return new ValidationResult(true, []);
  }

  static failure(errors) {
    return new ValidationResult(false, errors);
  }
}

/**
 * Email validation regex (RFC 5322 compliant)
 */
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * Bulgarian phone number patterns
 */
const BG_PHONE_PATTERNS = {
  // Mobile: 08XX XXX XXX or 08XXXXXXXX
  mobile: /^08[789]\d{7}$/,
  // Mobile with country code: +3598XX XXX XXX
  mobileInternational: /^\+3598[789]\d{7}$/,
  // Landline Sofia: 02 XXX XXXX or 02XXXXXXXX
  landlineSofia: /^02\d{7}$/,
  // Landline with area code
  landline: /^0\d{8,9}$/,
  // Landline international: +359X XXX XXXX
  landlineInternational: /^\+359\d{8,9}$/
};

/**
 * Bulgarian phone number regex (accepts multiple formats)
 */
const BG_PHONE_REGEX = /^(?:\+359|0)?[1-9]\d{7,8}$/;

/**
 * Password strength requirements
 */
export const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: false
};

/**
 * Betting validation limits
 */
export const BETTING_LIMITS = {
  MIN_STAKE: 0.01,
  MAX_STAKE: 100000,
  MIN_ODDS: 1.01,
  MAX_ODDS: 1000,
  MIN_PERCENTAGE: 0,
  MAX_PERCENTAGE: 100
};

/**
 * Error message translations
 */
const ERROR_MESSAGES = {
  bg: {
    required: 'Това поле е задължително',
    email: 'Невалиден имейл адрес',
    emailInvalid: 'Въведете валиден имейл адрес',
    password: 'Паролата е задължителна',
    passwordTooShort: 'Паролата трябва да е поне {min} символа',
    passwordTooLong: 'Паролата не може да е повече от {max} символа',
    passwordNoUppercase: 'Паролата трябва да съдържа поне една главна буква',
    passwordNoLowercase: 'Паролата трябва да съдържа поне една малка буква',
    passwordNoNumbers: 'Паролата трябва да съдържа поне едно число',
    passwordNoSpecialChars: 'Паролата трябва да съдържа поне един специален символ',
    passwordWeak: 'Паролата е твърде слаба',
    passwordMedium: 'Паролата е средна',
    passwordStrong: 'Паролата е силна',
    passwordsDoNotMatch: 'Паролите не съвпадат',
    phone: 'Невалиден телефонен номер',
    phoneInvalid: 'Въведете валиден български телефонен номер',
    phoneFormat: 'Телефонният номер трябва да е във формат: 08XX XXX XXX или +3598XX XXX XXX',
    stake: 'Невалидна сума за залагане',
    stakeTooLow: 'Сумата за залагане трябва да е поне {min} лв',
    stakeTooHigh: 'Сумата за залагане не може да е повече от {max} лв',
    stakeInvalid: 'Въведете валидна сума за залагане',
    odds: 'Невалиден коефициент',
    oddsTooLow: 'Коефициентът трябва да е поне {min}',
    oddsTooHigh: 'Коефициентът не може да е повече от {max}',
    oddsInvalid: 'Въведете валиден коефициент',
    percentage: 'Невалиден процент',
    percentageTooLow: 'Процентът трябва да е поне {min}%',
    percentageTooHigh: 'Процентът не може да е повече от {max}%',
    percentageInvalid: 'Въведете валиден процент',
    number: 'Невалидно число',
    numberTooLow: 'Стойността трябва да е поне {min}',
    numberTooHigh: 'Стойността не може да е повече от {max}',
    numberInvalid: 'Въведете валидно число',
    positive: 'Стойността трябва да е положителна',
    nonNegative: 'Стойността не може да е отрицателна',
    integer: 'Стойността трябва да е цяло число',
    url: 'Невалиден URL адрес',
    urlInvalid: 'Въведете валиден URL адрес',
    date: 'Невалидна дата',
    dateInvalid: 'Въведете валидна дата',
    datePast: 'Датата не може да е в миналото',
    dateFuture: 'Датата не може да е в бъдещето',
    dateRange: 'Датата трябва да е между {start} и {end}',
    minLength: 'Полето трябва да съдържа поне {min} символа',
    maxLength: 'Полето не може да съдържа повече от {max} символа',
    exactLength: 'Полето трябва да съдържа точно {length} символа',
    pattern: 'Стойността не отговаря на изисквания формат',
    custom: '{message}'
  },
  en: {
    required: 'This field is required',
    email: 'Invalid email address',
    emailInvalid: 'Please enter a valid email address',
    password: 'Password is required',
    passwordTooShort: 'Password must be at least {min} characters',
    passwordTooLong: 'Password cannot be more than {max} characters',
    passwordNoUppercase: 'Password must contain at least one uppercase letter',
    passwordNoLowercase: 'Password must contain at least one lowercase letter',
    passwordNoNumbers: 'Password must contain at least one number',
    passwordNoSpecialChars: 'Password must contain at least one special character',
    passwordWeak: 'Password is too weak',
    passwordMedium: 'Password is medium strength',
    passwordStrong: 'Password is strong',
    passwordsDoNotMatch: 'Passwords do not match',
    phone: 'Invalid phone number',
    phoneInvalid: 'Please enter a valid Bulgarian phone number',
    phoneFormat: 'Phone number must be in format: 08XX XXX XXX or +3598XX XXX XXX',
    stake: 'Invalid stake amount',
    stakeTooLow: 'Stake must be at least {min} BGN',
    stakeTooHigh: 'Stake cannot be more than {max} BGN',
    stakeInvalid: 'Please enter a valid stake amount',
    odds: 'Invalid odds',
    oddsTooLow: 'Odds must be at least {min}',
    oddsTooHigh: 'Odds cannot be more than {max}',
    oddsInvalid: 'Please enter valid odds',
    percentage: 'Invalid percentage',
    percentageTooLow: 'Percentage must be at least {min}%',
    percentageTooHigh: 'Percentage cannot be more than {max}%',
    percentageInvalid: 'Please enter a valid percentage',
    number: 'Invalid number',
    numberTooLow: 'Value must be at least {min}',
    numberTooHigh: 'Value cannot be more than {max}',
    numberInvalid: 'Please enter a valid number',
    positive: 'Value must be positive',
    nonNegative: 'Value cannot be negative',
    integer: 'Value must be an integer',
    url: 'Invalid URL address',
    urlInvalid: 'Please enter a valid URL address',
    date: 'Invalid date',
    dateInvalid: 'Please enter a valid date',
    datePast: 'Date cannot be in the past',
    dateFuture: 'Date cannot be in the future',
    dateRange: 'Date must be between {start} and {end}',
    minLength: 'Field must contain at least {min} characters',
    maxLength: 'Field cannot contain more than {max} characters',
    exactLength: 'Field must contain exactly {length} characters',
    pattern: 'Value does not match required format',
    custom: '{message}'
  }
};

/**
 * ValidationUtils class with comprehensive validation functions
 */
class ValidationUtils {
  /**
   * Validate if a value is not empty
   * @param {any} value - Value to validate
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {ValidationResult} Validation result
   */
  static validateRequired(value, language = 'bg') {
    if (value === null || value === undefined || value === '') {
      return ValidationResult.failure(this.getMessage('required', language));
    }
    
    if (typeof value === 'string' && value.trim() === '') {
      return ValidationResult.failure(this.getMessage('required', language));
    }
    
    return ValidationResult.success();
  }

  /**
   * Validate email address
   * @param {string} email - Email to validate
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {ValidationResult} Validation result
   */
  static validateEmail(email, language = 'bg') {
    if (!email) {
      return ValidationResult.failure(this.getMessage('emailInvalid', language));
    }
    
    const emailStr = String(email).trim().toLowerCase();
    
    if (emailStr.length === 0) {
      return ValidationResult.failure(this.getMessage('emailInvalid', language));
    }
    
    if (!EMAIL_REGEX.test(emailStr)) {
      return ValidationResult.failure(this.getMessage('emailInvalid', language));
    }
    
    // Additional checks
    if (emailStr.indexOf('..') !== -1) {
      return ValidationResult.failure(this.getMessage('emailInvalid', language));
    }
    
    const parts = emailStr.split('@');
    if (parts.length !== 2) {
      return ValidationResult.failure(this.getMessage('emailInvalid', language));
    }
    
    if (parts[0].length === 0 || parts[1].length === 0) {
      return ValidationResult.failure(this.getMessage('emailInvalid', language));
    }
    
    return ValidationResult.success();
  }

  /**
   * Check password strength
   * @param {string} password - Password to check
   * @returns {Object} Strength object with score and feedback
   */
  static checkPasswordStrength(password) {
    if (!password || password.length === 0) {
      return { score: 0, strength: 'none', feedback: [] };
    }
    
    let score = 0;
    const feedback = [];
    
    // Length check
    if (password.length >= 8) {
      score += 1;
    } else {
      feedback.push('passwordTooShort');
    }
    
    if (password.length >= 12) {
      score += 1;
    }
    
    // Uppercase check
    if (/[A-Z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('passwordNoUppercase');
    }
    
    // Lowercase check
    if (/[a-z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('passwordNoLowercase');
    }
    
    // Numbers check
    if (/\d/.test(password)) {
      score += 1;
    } else {
      feedback.push('passwordNoNumbers');
    }
    
    // Special characters check
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      score += 1;
    } else {
      feedback.push('passwordNoSpecialChars');
    }
    
    // Determine strength
    let strength = 'weak';
    if (score >= 5) {
      strength = 'strong';
    } else if (score >= 3) {
      strength = 'medium';
    }
    
    return { score, strength, feedback };
  }

  /**
   * Validate password
   * @param {string} password - Password to validate
   * @param {Object} requirements - Password requirements
   * @param {number} requirements.minLength - Minimum length
   * @param {number} requirements.maxLength - Maximum length
   * @param {boolean} requirements.requireUppercase - Require uppercase
   * @param {boolean} requirements.requireLowercase - Require lowercase
   * @param {boolean} requirements.requireNumbers - Require numbers
   * @param {boolean} requirements.requireSpecialChars - Require special chars
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {ValidationResult} Validation result
   */
  static validatePassword(password, requirements = {}, language = 'bg') {
    const reqs = { ...PASSWORD_REQUIREMENTS, ...requirements };
    const result = new ValidationResult(true, []);
    
    if (!password) {
      return ValidationResult.failure(this.getMessage('password', language));
    }
    
    // Length validation
    if (password.length < reqs.minLength) {
      result.addError(this.getMessage('passwordTooShort', language, { min: reqs.minLength }));
    }
    
    if (reqs.maxLength && password.length > reqs.maxLength) {
      result.addError(this.getMessage('passwordTooLong', language, { max: reqs.maxLength }));
    }
    
    // Character requirements
    if (reqs.requireUppercase && !/[A-Z]/.test(password)) {
      result.addError(this.getMessage('passwordNoUppercase', language));
    }
    
    if (reqs.requireLowercase && !/[a-z]/.test(password)) {
      result.addError(this.getMessage('passwordNoLowercase', language));
    }
    
    if (reqs.requireNumbers && !/\d/.test(password)) {
      result.addError(this.getMessage('passwordNoNumbers', language));
    }
    
    if (reqs.requireSpecialChars && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      result.addError(this.getMessage('passwordNoSpecialChars', language));
    }
    
    return result;
  }

  /**
   * Validate password confirmation
   * @param {string} password - Original password
   * @param {string} confirmPassword - Confirmation password
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {ValidationResult} Validation result
   */
  static validatePasswordConfirm(password, confirmPassword, language = 'bg') {
    if (!password || !confirmPassword) {
      return ValidationResult.failure(this.getMessage('required', language));
    }
    
    if (password !== confirmPassword) {
      return ValidationResult.failure(this.getMessage('passwordsDoNotMatch', language));
    }
    
    return ValidationResult.success();
  }

  /**
   * Validate Bulgarian phone number
   * @param {string} phone - Phone number to validate
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {ValidationResult} Validation result
   */
  static validatePhoneBG(phone, language = 'bg') {
    if (!phone) {
      return ValidationResult.failure(this.getMessage('phoneInvalid', language));
    }
    
    // Remove spaces, dashes, and parentheses
    const cleaned = String(phone).replace(/[\s\-()]/g, '');
    
    // Check mobile patterns
    if (BG_PHONE_PATTERNS.mobile.test(cleaned) || 
        BG_PHONE_PATTERNS.mobileInternational.test(cleaned)) {
      return ValidationResult.success();
    }
    
    // Check landline patterns
    if (BG_PHONE_PATTERNS.landline.test(cleaned) || 
        BG_PHONE_PATTERNS.landlineInternational.test(cleaned)) {
      return ValidationResult.success();
    }
    
    // General pattern check
    if (BG_PHONE_REGEX.test(cleaned)) {
      return ValidationResult.success();
    }
    
    return ValidationResult.failure(this.getMessage('phoneInvalid', language));
  }

  /**
   * Validate stake amount for betting
   * @param {number|string} stake - Stake amount to validate
   * @param {Object} limits - Stake limits
   * @param {number} limits.min - Minimum stake (default: BETTING_LIMITS.MIN_STAKE)
   * @param {number} limits.max - Maximum stake (default: BETTING_LIMITS.MAX_STAKE)
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {ValidationResult} Validation result
   */
  static validateStake(stake, limits = {}, language = 'bg') {
    const min = limits.min !== undefined ? limits.min : BETTING_LIMITS.MIN_STAKE;
    const max = limits.max !== undefined ? limits.max : BETTING_LIMITS.MAX_STAKE;
    
    const stakeNum = parseFloat(stake);
    
    if (isNaN(stakeNum) || !isFinite(stakeNum)) {
      return ValidationResult.failure(this.getMessage('stakeInvalid', language));
    }
    
    if (stakeNum < min) {
      return ValidationResult.failure(this.getMessage('stakeTooLow', language, { min }));
    }
    
    if (stakeNum > max) {
      return ValidationResult.failure(this.getMessage('stakeTooHigh', language, { max }));
    }
    
    return ValidationResult.success();
  }

  /**
   * Validate betting odds
   * @param {number|string} odds - Odds to validate
   * @param {Object} limits - Odds limits
   * @param {number} limits.min - Minimum odds (default: BETTING_LIMITS.MIN_ODDS)
   * @param {number} limits.max - Maximum odds (default: BETTING_LIMITS.MAX_ODDS)
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {ValidationResult} Validation result
   */
  static validateOdds(odds, limits = {}, language = 'bg') {
    const min = limits.min !== undefined ? limits.min : BETTING_LIMITS.MIN_ODDS;
    const max = limits.max !== undefined ? limits.max : BETTING_LIMITS.MAX_ODDS;
    
    const oddsNum = parseFloat(odds);
    
    if (isNaN(oddsNum) || !isFinite(oddsNum)) {
      return ValidationResult.failure(this.getMessage('oddsInvalid', language));
    }
    
    if (oddsNum < min) {
      return ValidationResult.failure(this.getMessage('oddsTooLow', language, { min }));
    }
    
    if (oddsNum > max) {
      return ValidationResult.failure(this.getMessage('oddsTooHigh', language, { max }));
    }
    
    return ValidationResult.success();
  }

  /**
   * Validate percentage value
   * @param {number|string} percentage - Percentage to validate
   * @param {Object} limits - Percentage limits
   * @param {number} limits.min - Minimum percentage (default: BETTING_LIMITS.MIN_PERCENTAGE)
   * @param {number} limits.max - Maximum percentage (default: BETTING_LIMITS.MAX_PERCENTAGE)
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {ValidationResult} Validation result
   */
  static validatePercentage(percentage, limits = {}, language = 'bg') {
    const min = limits.min !== undefined ? limits.min : BETTING_LIMITS.MIN_PERCENTAGE;
    const max = limits.max !== undefined ? limits.max : BETTING_LIMITS.MAX_PERCENTAGE;
    
    const percentageNum = parseFloat(percentage);
    
    if (isNaN(percentageNum) || !isFinite(percentageNum)) {
      return ValidationResult.failure(this.getMessage('percentageInvalid', language));
    }
    
    if (percentageNum < min) {
      return ValidationResult.failure(this.getMessage('percentageTooLow', language, { min }));
    }
    
    if (percentageNum > max) {
      return ValidationResult.failure(this.getMessage('percentageTooHigh', language, { max }));
    }
    
    return ValidationResult.success();
  }

  /**
   * Validate number
   * @param {number|string} value - Number to validate
   * @param {Object} options - Validation options
   * @param {number} options.min - Minimum value
   * @param {number} options.max - Maximum value
   * @param {boolean} options.positive - Must be positive
   * @param {boolean} options.nonNegative - Must be non-negative
   * @param {boolean} options.integer - Must be integer
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {ValidationResult} Validation result
   */
  static validateNumber(value, options = {}, language = 'bg') {
    const num = parseFloat(value);
    
    if (isNaN(num) || !isFinite(num)) {
      return ValidationResult.failure(this.getMessage('numberInvalid', language));
    }
    
    if (options.integer && !Number.isInteger(num)) {
      return ValidationResult.failure(this.getMessage('integer', language));
    }
    
    if (options.positive && num <= 0) {
      return ValidationResult.failure(this.getMessage('positive', language));
    }
    
    if (options.nonNegative && num < 0) {
      return ValidationResult.failure(this.getMessage('nonNegative', language));
    }
    
    if (options.min !== undefined && num < options.min) {
      return ValidationResult.failure(this.getMessage('numberTooLow', language, { min: options.min }));
    }
    
    if (options.max !== undefined && num > options.max) {
      return ValidationResult.failure(this.getMessage('numberTooHigh', language, { max: options.max }));
    }
    
    return ValidationResult.success();
  }

  /**
   * Validate string length
   * @param {string} value - String to validate
   * @param {Object} options - Validation options
   * @param {number} options.minLength - Minimum length
   * @param {number} options.maxLength - Maximum length
   * @param {number} options.exactLength - Exact length
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {ValidationResult} Validation result
   */
  static validateLength(value, options = {}, language = 'bg') {
    if (value === null || value === undefined) {
      value = '';
    }
    
    const str = String(value);
    const length = str.length;
    
    const result = new ValidationResult(true, []);
    
    if (options.exactLength !== undefined && length !== options.exactLength) {
      result.addError(this.getMessage('exactLength', language, { length: options.exactLength }));
    }
    
    if (options.minLength !== undefined && length < options.minLength) {
      result.addError(this.getMessage('minLength', language, { min: options.minLength }));
    }
    
    if (options.maxLength !== undefined && length > options.maxLength) {
      result.addError(this.getMessage('maxLength', language, { max: options.maxLength }));
    }
    
    return result;
  }

  /**
   * Validate URL
   * @param {string} url - URL to validate
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {ValidationResult} Validation result
   */
  static validateURL(url, language = 'bg') {
    if (!url || url.trim() === '') {
      return ValidationResult.failure(this.getMessage('urlInvalid', language));
    }
    
    try {
      const urlObj = new URL(url);
      if (!urlObj.protocol || !urlObj.hostname) {
        return ValidationResult.failure(this.getMessage('urlInvalid', language));
      }
      return ValidationResult.success();
    } catch (error) {
      return ValidationResult.failure(this.getMessage('urlInvalid', language));
    }
  }

  /**
   * Validate date
   * @param {string|Date} date - Date to validate
   * @param {Object} options - Validation options
   * @param {boolean} options.allowPast - Allow past dates
   * @param {boolean} options.allowFuture - Allow future dates
   * @param {Date} options.minDate - Minimum date
   * @param {Date} options.maxDate - Maximum date
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {ValidationResult} Validation result
   */
  static validateDate(date, options = {}, language = 'bg') {
    let dateObj;
    
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else {
      return ValidationResult.failure(this.getMessage('dateInvalid', language));
    }
    
    if (isNaN(dateObj.getTime())) {
      return ValidationResult.failure(this.getMessage('dateInvalid', language));
    }
    
    const now = new Date();
    const result = new ValidationResult(true, []);
    
    if (options.allowPast === false && dateObj < now) {
      result.addError(this.getMessage('datePast', language));
    }
    
    if (options.allowFuture === false && dateObj > now) {
      result.addError(this.getMessage('dateFuture', language));
    }
    
    if (options.minDate && dateObj < options.minDate) {
      result.addError(this.getMessage('dateRange', language, {
        start: options.minDate.toLocaleDateString(),
        end: options.maxDate ? options.maxDate.toLocaleDateString() : 'now'
      }));
    }
    
    if (options.maxDate && dateObj > options.maxDate) {
      result.addError(this.getMessage('dateRange', language, {
        start: options.minDate ? options.minDate.toLocaleDateString() : 'now',
        end: options.maxDate.toLocaleDateString()
      }));
    }
    
    return result;
  }

  /**
   * Validate against a pattern/regex
   * @param {string} value - Value to validate
   * @param {RegExp|string} pattern - Pattern or regex
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {ValidationResult} Validation result
   */
  static validatePattern(value, pattern, language = 'bg') {
    if (value === null || value === undefined) {
      return ValidationResult.failure(this.getMessage('pattern', language));
    }
    
    const regex = pattern instanceof RegExp ? pattern : new RegExp(pattern);
    
    if (!regex.test(String(value))) {
      return ValidationResult.failure(this.getMessage('pattern', language));
    }
    
    return ValidationResult.success();
  }

  /**
   * Validate multiple fields at once
   * @param {Object} validations - Object with field validations
   * @returns {Object} Validation results object
   */
  static validateFields(validations) {
    const results = {};
    
    for (const [field, validation] of Object.entries(validations)) {
      if (typeof validation === 'function') {
        results[field] = validation();
      } else if (validation instanceof ValidationResult) {
        results[field] = validation;
      }
    }
    
    return results;
  }

  /**
   * Check if all validations passed
   * @param {Object|Array} results - Validation results
   * @returns {boolean} True if all validations passed
   */
  static isValid(results) {
    if (Array.isArray(results)) {
      return results.every(r => r instanceof ValidationResult ? r.isValid : false);
    }
    
    if (typeof results === 'object') {
      return Object.values(results).every(r => r instanceof ValidationResult ? r.isValid : true);
    }
    
    return results instanceof ValidationResult ? results.isValid : false;
  }

  /**
   * Get all errors from validation results
   * @param {Object|Array} results - Validation results
   * @returns {Array} Array of all error messages
   */
  static getErrors(results) {
    const errors = [];
    
    if (Array.isArray(results)) {
      results.forEach(r => {
        if (r instanceof ValidationResult && !r.isValid) {
          errors.push(...r.errors);
        }
      });
    } else if (typeof results === 'object') {
      Object.values(results).forEach(r => {
        if (r instanceof ValidationResult && !r.isValid) {
          errors.push(...r.errors);
        }
      });
    } else if (results instanceof ValidationResult && !results.isValid) {
      errors.push(...results.errors);
    }
    
    return errors;
  }

  /**
   * Get error message
   * @param {string} key - Error message key
   * @param {string} language - Language code ('bg' or 'en')
   * @param {Object} params - Parameters to replace in message
   * @returns {string} Error message
   */
  static getMessage(key, language = 'bg', params = {}) {
    const messages = ERROR_MESSAGES[language] || ERROR_MESSAGES.bg;
    let message = messages[key] || messages.custom || key;
    
    // Replace parameters
    Object.entries(params).forEach(([param, value]) => {
      message = message.replace(`{${param}}`, value);
    });
    
    return message;
  }

  /**
   * Create custom validation error
   * @param {string} message - Error message
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {ValidationResult} Validation result with custom error
   */
  static customError(message, language = 'bg') {
    return ValidationResult.failure(this.getMessage('custom', language, { message }));
  }

  /**
   * Combine multiple validation results
   * @param {...ValidationResult} results - Validation results to combine
   * @returns {ValidationResult} Combined validation result
   */
  static combine(...results) {
    const errors = [];
    let isValid = true;
    
    results.forEach(result => {
      if (result instanceof ValidationResult) {
        if (!result.isValid) {
          isValid = false;
          errors.push(...result.errors);
        }
      }
    });
    
    return new ValidationResult(isValid, errors);
  }
}

// Export the ValidationUtils class
// Note: ValidationResult is already exported above
export default ValidationUtils;

